import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, output, input, viewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { compareTwoStrings } from 'string-similarity';

interface Camera {
  ba_id: string | null;
  ba_name: string | null;
  camera_name: string | null;
  camera_uuid: string | null;
  client_name: string | null;
  client_uuid: string | null;
  la_id: string | null;
  la_name: string | null;
  lc_id: string | null;
  orig_uuid: string | null;
}

export interface OrgUuidOption {
  clientName: string;
  orgUUID: string;
}

@Component({
  selector: 'app-org-uuid-field',
  templateUrl: './org-uuid-field.component.html',
  styleUrls: ['./org-uuid-field.component.scss'],
  standalone: false,
})
export class OrgUuidFieldComponent implements AfterViewInit, OnDestroy, OnInit, OnChanges {
  @Input() cameras: Camera[];
  @Input() branchName: string;
  readonly orgUuid = input<string>('');
  // If this is true then we behave like the EditableLabel component
  readonly showEditToggle = input(false);
  @Input() isUpdating = false;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  readonly onSave = output<OrgUuidOption | null>();

  originalValue: string = '';

  errorMessage: string = '';

  isEditing = true;

  // Handle to our MatInput FormControl (for getting / setting values manually)
  control = new UntypedFormControl();
  // These are ALL ORG_UUIDs (with Client Name) that we have in our system;
  // the admin does not need to see everything (hence the filter below)
  availableOptions: OrgUuidOption[] = [];
  // These are the ORG_UUIDs (with Client Name) that the admin can choose from
  filteredOptions: Observable<OrgUuidOption[]>;

  // Variables for listening to MatAutoComplete behaviors
  autoCompleteCloseSub: Subscription;
  readonly autoCompleteTrigger = viewChild(MatAutocompleteTrigger);

  get value(): OrgUuidOption | null {
    return this.control.value;
  }

  constructor() {}

  ngOnInit(): void {
    // If showEditToggle is true then start with editMode disabled.
    // User must click on our edit toggle button to start editing.
    this.isEditing = this.showEditToggle() ? false : true;
    this.availableOptions = [
      ...new Set(
        this.cameras
          .filter((c) => !c.orig_uuid)
          .map((c) => ({ clientName: c.client_name, orgUUID: c.client_uuid }))
          // This is needed for "new Set" to properly remove duplicates
          .map((o) => JSON.stringify(o)),
      ),
    ]
      // Cast our elements back to OrgUuidOption (because it was serialized above)
      .map<OrgUuidOption>((o) => JSON.parse(o as string));

    this.filteredOptions = this.control.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterOptions(value)),
    );

    const initialOption = this.availableOptions.find((o) => o.orgUUID === this.orgUuid());
    if (initialOption) {
      this.control.setValue(initialOption);
      this.originalValue = this.orgUuid();
    }
  }

  ngAfterViewInit() {
    this.subscribeToClosingActions();
  }

  // Parent components pass [orgUuid] representing the original value
  ngOnChanges(changes: SimpleChanges): void {
    // Update our ORG_UUID value if there are any changes
    if (changes.orgUuid && changes.orgUuid.currentValue !== changes.orgUuid.previousValue) {
      const option = this.availableOptions.find((o) => o.orgUUID === this.orgUuid());
      if (!option) return;
      this.control.setValue(option);
      this.originalValue = this.orgUuid();
    }
  }

  ngOnDestroy() {
    if (this.autoCompleteCloseSub && !this.autoCompleteCloseSub.closed) {
      this.autoCompleteCloseSub.unsubscribe();
    }
  }

  public success() {
    this.isUpdating = false;
    this.errorMessage = '';
    this.isEditing = false;
  }

  public error(message: string) {
    this.isUpdating = false;
    this.errorMessage = message;
  }

  // ------------------- Handlers ------------------- //

  handleEdit() {
    this.isEditing = !this.isEditing;
  }

  handleCancel() {
    this.isEditing = false;
    // If the user changed the option / value in any way, it is all reverted to the original value here.
    const option = this.availableOptions.find((o) => o.orgUUID === this.originalValue);
    if (option) this.control.setValue(option);
  }

  handleSave() {
    this.onSave.emit(this.value);
  }

  // ------------------- MatAutocomplete related functions ------------------- //

  onOptionSelect(event: MatAutocompleteSelectedEvent): void {
    this.control.setValue(event.option.value);
  }

  displayWith(option: OrgUuidOption) {
    return option?.orgUUID ? option.orgUUID : '';
  }

  private filterOptions(filterValue: string): OrgUuidOption[] {
    return (
      this.availableOptions
        // If "filterValue" is empty then skip this filter
        .filter((u) =>
          !filterValue ? true : u.clientName?.toLowerCase().includes(filterValue) || u.orgUUID?.toLowerCase().includes(filterValue),
        )
        // Sort alphabetically by ORG UUID's client name
        .sort((a, b) => compareTwoStrings(b.clientName ?? '', this.branchName) - compareTwoStrings(a.clientName ?? '', this.branchName))
    );
  }

  private subscribeToClosingActions(): void {
    if (this.autoCompleteCloseSub && !this.autoCompleteCloseSub.closed) {
      this.autoCompleteCloseSub.unsubscribe();
    }

    this.autoCompleteCloseSub = this.autoCompleteTrigger().panelClosingActions.subscribe(
      (e) => {
        if (!e || !e.source) {
          this.control.setValue(null);
        }
      },
      // Not actually sure why these are needed
      () => this.subscribeToClosingActions(),
      () => this.subscribeToClosingActions(),
    );
  }
}
